<template>
  <header class="hub-header" :class="{ 'is-fixed': fixed }">
    <div class="hub-header__inner">
      <router-link to="/" class="hub-brand" :title="fullName">
        <!-- M3D HUB 심볼(Tier Stack). 원본: docs/logo-m3d/final/m3d-mark.svg -->
        <svg class="hub-brand__mark" viewBox="14 17 92 92" aria-hidden="true">
          <polygon points="16,78 60,100 60,107 16,85" fill="#002a47" />
          <polygon points="104,78 60,100 60,107 104,85" fill="#00335a" />
          <polygon points="16,78 60,56 104,78 60,100" fill="#003f67" />
          <polygon points="16,60 60,82 60,89 16,67" fill="#0a5f90" />
          <polygon points="104,60 60,82 60,89 104,67" fill="#096aa0" />
          <polygon points="16,60 60,38 104,60 60,82" fill="#0c78b3" />
          <polygon points="16,42 60,64 60,71 16,49" fill="#2aa6e0" />
          <polygon points="104,42 60,64 60,71 104,49" fill="#38b4ec" />
          <polygon points="16,42 60,20 104,42 60,64" fill="#4ec8ff" />
          <ellipse cx="60" cy="42" rx="9" ry="4.5" fill="#fff" />
        </svg>
        <span class="hub-brand__text">
          <b>M3D HUB</b>
          <small>Monolithic 3D Semiconductor Hub</small>
        </span>
      </router-link>

      <nav class="hub-gnb" aria-label="주요 메뉴">
        <template v-for="item in menus">
          <span
            v-if="item.disabled"
            :key="item.key"
            class="hub-gnb__item is-disabled"
            :title="item.hint"
          >{{ item.name }}<em v-if="item.badge">{{ item.badge }}</em></span>
          <a
            v-else
            :key="item.key"
            href="#"
            class="hub-gnb__item"
            :class="{ 'is-active': item.active }"
            @click.prevent="go(item)"
          >{{ item.name }}</a>
        </template>
      </nav>

      <div class="hub-header__right">
        <template v-if="user">
          <button type="button" class="hub-user" title="마이페이지" @click="go({ path: '/account/mypage' })">
            <span class="hub-user__avatar">{{ initial }}</span>
            <span class="hub-user__name">{{ user.name }}</span>
            <span class="hub-user__role">{{ roleLabel }}</span>
          </button>
          <button type="button" class="hub-btn hub-btn--ghost" @click="logout">로그아웃</button>
        </template>
        <template v-else>
          <button type="button" class="hub-btn hub-btn--ghost hub-only-wide" @click="go({ path: '/account/signup' })">회원가입</button>
          <button type="button" class="hub-btn hub-btn--primary" @click="go({ path: '/account/login' })">로그인</button>
        </template>
        <button
          type="button"
          class="hub-menu-btn"
          :class="{ 'is-open': mobileOpen }"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          @click.stop="mobileOpen = !mobileOpen"
        ><i></i>메뉴</button>
      </div>
    </div>

    <div v-if="mobileOpen" class="hub-mobile" v-click-outside="closeMobile">
      <template v-for="item in menus">
        <span v-if="item.disabled" :key="item.key" class="hub-mobile__item is-disabled">{{ item.name }}<em v-if="item.badge">{{ item.badge }}</em></span>
        <a v-else :key="item.key" href="#" class="hub-mobile__item" :class="{ 'is-active': item.active }" @click.prevent="go(item)">{{ item.name }}</a>
      </template>
      <div class="hub-mobile__foot">
        <template v-if="user">
          <a href="#" @click.prevent="go({ path: '/account/mypage' })">마이페이지</a>
          <a href="#" @click.prevent="logout">로그아웃</a>
        </template>
        <template v-else>
          <a href="#" @click.prevent="go({ path: '/account/login' })">로그인</a>
          <a href="#" @click.prevent="go({ path: '/account/signup' })">회원가입</a>
        </template>
      </div>
    </div>
  </header>
</template>

<script>
import vClickOutside from "v-click-outside";
import getToken from "@/plugins/wApi/lib/getToken";

const ROLE_LABELS = [
  ["super", "최고관리자"],
  ["admin", "관리자"],
  ["generalManager", "총괄관리자"],
  ["committee", "위원"],
  ["assistant", "간사"],
];

export default {
  name: "HubHeader",
  directives: { clickOutside: vClickOutside.directive },
  props: {
    fixed: { type: Boolean, default: true },
  },
  data() {
    return {
      mobileOpen: false,
      fullName: "M3D 고집적 산화물계 반도체 소자 구현 및 소재-공정 전주기 데이터 HUB",
    };
  },
  computed: {
    user() {
      const u = this.$store.state.user;
      return u && !u.error ? u : null;
    },
    canEdit() {
      const u = this.user;
      return !!u && !!(u.admin || u.assistant || u.committee || u.super || u.generalManager);
    },
    roleLabel() {
      const u = this.user;
      if (!u) return "";
      const hit = ROLE_LABELS.find(([key]) => u[key]);
      return hit ? hit[1] : "일반";
    },
    initial() {
      return String(this.user?.name || "?").trim().charAt(0);
    },
    menus() {
      const p = this.$route.path;
      const editHint = this.user ? "간사·위원 이상만 사용할 수 있습니다" : "로그인 후 사용할 수 있습니다";
      return [
        { key: "home", name: "홈", path: "/", active: p === "/" || p === "/platform" },
        { key: "experiment", name: "실험입력", path: "/experiment/gate", query: { editorType: "gate" },
          active: p.startsWith("/experiment") || p.startsWith("/process") || p.startsWith("/protocol"),
          disabled: !this.canEdit, hint: editHint },
        { key: "develope", name: "계산입력", disabled: true, badge: "준비중", hint: "개발 중입니다" },
        // 시각화 = 데이터 시각화 페이지(/data-visualization: 데이터 연관관계·지식그래프). 레거시 /visualization/gate 는 홈·GNB 에서 연결하지 않음(에디터 내부 링크만)
        { key: "visualization", name: "시각화", path: "/data-visualization",
          active: p.startsWith("/data-visualization") || p.startsWith("/visualization"),
          disabled: !this.user, hint: "로그인 후 사용할 수 있습니다" },
        { key: "matdb", name: "데이터 검색", href: process.env.BASE_URL + "matdb/",
          active: false, disabled: !this.user, hint: "로그인 후 사용할 수 있습니다" },
      ];
    },
  },
  watch: {
    $route() {
      this.mobileOpen = false;
    },
  },
  mounted() {
    if (!this.user && getToken().Authorization) this.getMe();
  },
  methods: {
    async getMe() {
      try {
        const user = await this.$wApi.user.me();
        this.$store.commit("setState", ["user", user]);
      } catch (e) {
        this.$store.commit("setState", ["user", null]);
      }
    },
    closeMobile() {
      this.mobileOpen = false;
    },
    go(item) {
      this.mobileOpen = false;
      if (item.href) {
        location.href = item.href;
        return;
      }
      this.$router.push({ path: item.path, query: item.query });
    },
    async logout() {
      this.mobileOpen = false;
      const ok = await window.confirm("로그아웃 하시겠습니까?");
      if (ok) this.$router.push("/account/logout");
    },
  },
};
</script>

<style scoped>
.hub-header {
  --hub-navy: #003f67;
  --hub-cyan: #4ec8ff;
  --hub-ink: #1a2236;
  --hub-text: #3c4656;
  --hub-muted: #6b7486;
  --hub-dim: #a4acb8;
  --hub-line: #e3e7ee;
  --hub-tint: #e9f1f9;
  --hub-tint-hover: #f3f6fa;
  --hub-h: 60px;
  position: relative;
  z-index: 9999;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid var(--hub-line);
  color: var(--hub-ink);
  font-family: "Pretendard", "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", sans-serif;
}
.hub-header.is-fixed {
  position: fixed;
  top: 0;
  left: 0;
}
.hub-header__inner {
  display: flex;
  align-items: center;
  gap: 8px;
  height: var(--hub-h);
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
}

/* brand */
.hub-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 18px;
  text-decoration: none !important;
  white-space: nowrap;
}
.hub-brand__mark {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
}
.hub-brand__text {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}
.hub-brand__text b {
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.01em;
  color: var(--hub-navy);
}
.hub-brand__text small {
  margin-top: 2px;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--hub-muted);
}

/* gnb */
.hub-gnb {
  display: flex;
  align-items: center;
  gap: 4px;
}
.hub-gnb__item {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 13px;
  border-radius: 8px;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--hub-text);
  text-decoration: none !important;
  white-space: nowrap;
  transition: background-color 0.15s, color 0.15s;
}
.hub-gnb__item:hover {
  background: var(--hub-tint-hover);
  color: var(--hub-navy);
}
.hub-gnb__item.is-active {
  background: var(--hub-tint);
  color: var(--hub-navy);
  font-weight: 600;
}
.hub-gnb__item.is-disabled {
  color: var(--hub-dim);
  cursor: default;
}
.hub-gnb__item.is-disabled:hover {
  background: none;
  color: var(--hub-dim);
}
.hub-gnb__item em,
.hub-mobile__item em {
  margin-left: 5px;
  padding: 1px 5px;
  border-radius: 4px;
  background: #f0f2f6;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  color: #8b93a1;
}

/* right */
.hub-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.hub-user {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 10px 0 4px;
  border: 1px solid var(--hub-line);
  border-radius: 999px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--hub-ink);
  white-space: nowrap;
  cursor: pointer;
}
.hub-user:hover {
  border-color: #cfd6e0;
  background: var(--hub-tint-hover);
}
.hub-user__name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hub-user__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--hub-navy);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}
.hub-user__role {
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef2f7;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #4b5a70;
}
.hub-btn {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 13px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}
.hub-btn--ghost {
  border-color: var(--hub-line);
  background: #fff;
  color: var(--hub-text);
}
.hub-btn--ghost:hover {
  border-color: #cfd6e0;
  background: var(--hub-tint-hover);
}
.hub-btn--primary {
  background: var(--hub-navy);
  color: #fff;
}
.hub-btn--primary:hover {
  background: #00538a;
}
.hub-menu-btn {
  display: none;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--hub-line);
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: var(--hub-ink);
  cursor: pointer;
}
.hub-menu-btn i {
  position: relative;
  display: inline-block;
  width: 14px;
  height: 10px;
  border-top: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
}
.hub-menu-btn i::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 2px;
  border-top: 2px solid currentColor;
}
.hub-menu-btn.is-open {
  background: var(--hub-tint);
  border-color: transparent;
  color: var(--hub-navy);
}

/* mobile panel */
.hub-mobile {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  padding: 8px 12px 12px;
  background: #fff;
  border-bottom: 1px solid var(--hub-line);
  box-shadow: 0 12px 24px rgba(16, 24, 40, 0.08);
}
.hub-mobile__item {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--hub-text);
  text-decoration: none !important;
}
.hub-mobile__item.is-active {
  background: var(--hub-tint);
  color: var(--hub-navy);
  font-weight: 600;
}
.hub-mobile__item.is-disabled {
  color: var(--hub-dim);
}
.hub-mobile__foot {
  display: flex;
  gap: 18px;
  margin-top: 8px;
  padding: 12px 12px 0;
  border-top: 1px solid var(--hub-line);
}
.hub-mobile__foot a {
  font-size: 14px;
  font-weight: 600;
  color: var(--hub-navy);
  text-decoration: none !important;
}

.hub-header button:focus-visible,
.hub-header a:focus-visible {
  outline: 2px solid var(--hub-cyan);
  outline-offset: 2px;
}

@media (max-width: 1600px) {
  .hub-header__inner {
    padding: 0 20px;
  }
}
@media (max-width: 900px) {
  .hub-gnb,
  .hub-only-wide,
  .hub-user__role {
    display: none;
  }
  .hub-menu-btn {
    display: inline-flex;
  }
  .hub-brand__text small {
    display: none;
  }
}
@media (max-width: 600px) {
  .hub-header__inner {
    padding: 0 14px;
  }
  .hub-user__name {
    display: none;
  }
  .hub-user {
    padding-right: 4px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hub-gnb__item,
  .hub-btn {
    transition: none;
  }
}
</style>
